import { PreloaderCircular } from '@shared/ui/kit';
import stylex from '@stylexjs/stylex';
import { FC, useCallback, useMemo, useState } from 'react';
import { useClient } from 'urql';
import { AddButton } from '@shared/ui/add-button';
import { SegmentCard } from '@features/segment-card';
import { styles } from '../styles';
import { CommercialBlockParticipantsModal } from '../CommercialBlockParticipantsModal/index';
import { AddParticipantsModal, ISegmentParticipants } from '@features/add-participants-modal';
import { Ticket, TicketsFilterInput } from '@pages/event/api/useFetchTicketSegment';
import { FunnelIcon, TableIcon } from '@shared/assets/icons';
import { ModalFilter } from '@features/modal-filter';
import { ParticipantsActionsDropdown } from '@features/participants-actions-dropdown';
import { ListTreeIcon } from '@shared/assets/icons/list-tree';
import { colors } from '@shared/tokens/colors.stylex';
import { SegmensTabProps, InvitedParticipants } from '../../model/types';
import { createInitialSegment } from '../../lib/utils';
import { getMainGuestOptionsFromSegments } from '../../lib/getMainGuestOptionsFromSegments';
import { useToast } from '@shared/hooks/useToast';
import { FORM_QUERY, FormResponse } from '@entities/leadCollector/lib/hooks/useFetchForm';

export const SegmentsTab: FC<SegmensTabProps> = ({
  segments,
  onDeleteSegment,
  sendTickets,
  sendInvitations,
  onAddSegment,
  onTicketDelete,
  onMoveToSegment,
  registerRefetch,
  refetchSegmentsQuery,
  eventId,
  refetchCategories,
  eventDateEnd,
  eventDateStart,
  eventSettings,
  onFilterChange,
  refetchEvent,
  segmentsFetching = false,
  hasGeneratedLinks = true,
  formId,
  registrationLinksSendingStarted = false,
  onSendRegistration,
  registrationSending,
  isCBGroup = false,
  checkedUsers,
  setCheckedUsers,
}) => {
  const client = useClient();
  const { showToast } = useToast();

  const [invitedParticipants, setInvitedParticipants] = useState<InvitedParticipants[]>([]);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [update, setUpdate] = useState<boolean>(false);
  const [currentSegment, setCurrentSegment] = useState<ISegmentParticipants>(structuredClone(createInitialSegment()));
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [ticketFilter, setTicketFilter] = useState<TicketsFilterInput | undefined>(undefined);
  const [addedTicketIds, setAddedTicketIds] = useState<number[]>([]);
  const [isTreeView, setIsTreeView] = useState<boolean>(false);
  const [isCommercialBlockModalOpen, setIsCommercialBlockModalOpen] = useState<boolean>(false);
  const [commercialBlockFormData, setCommercialBlockFormData] = useState<FormResponse['getFormById'] | null>(null);
  const [commercialBlockTicketSegmentId, setCommercialBlockTicketSegmentId] = useState<number | undefined>(undefined);

  const mainGuestOptions = useMemo(() => getMainGuestOptionsFromSegments(segments), [segments]);

  const openModaleHandler = useCallback(
    async (segment: ISegmentParticipants, users: InvitedParticipants[]) => {
      if (!isCBGroup) {
        setInvitedParticipants(users);
        setIsOpenModal(true);
        setUpdate((prevUpdate) => !prevUpdate);
        setCurrentSegment(segment);
        return;
      }
      if (!hasGeneratedLinks) {
        setCommercialBlockFormData(null);
        setCommercialBlockTicketSegmentId(segment.id);
        setIsCommercialBlockModalOpen(true);
        return;
      }
      if (!formId) {
        showToast('Ошибка при загрузке данных', 'error');
        return;
      }
      try {
        const result = await client.query<FormResponse>(FORM_QUERY, { formId }).toPromise();
        if (result.error || !result.data?.getFormById) {
          showToast('Ошибка при загрузке данных', 'error');
          return;
        }
        setCommercialBlockFormData(result.data.getFormById);
        setCommercialBlockTicketSegmentId(segment.id);
        setIsCommercialBlockModalOpen(true);
      } catch {
        showToast('Ошибка при загрузке данных', 'error');
      }
    },
    [isCBGroup, hasGeneratedLinks, formId, client, showToast]
  );

  const toggleAccordionHandler = (isOpen: boolean) => {
    if (isOpen) {
      setUpdate(!update);
    }
  };

  const removeTicketFromSelection = (ticketId: number) => {
    setCheckedUsers((prev) => {
      if (!(ticketId in prev)) return prev;
      const next = { ...prev };
      delete next[ticketId];
      return next;
    });
    onTicketDelete?.(ticketId);
  };

  const toggleFilterModal = () => setIsFilterModalOpen((prev) => !prev);

  const toggleUser = (ticket: Ticket) => {
    const isChecked = !checkedUsers[ticket.id];
    setCheckedUsers((prev) => {
      const updated = { ...prev };
      const dependents = [...(ticket.dependents?.adult ?? []), ...(ticket.dependents?.child ?? [])];

      if (!isChecked) {
        delete updated[ticket.id];
        for (const dependent of dependents) {
          delete updated[dependent.id];
        }
        return updated;
      }

      updated[ticket.id] = ticket.participant.id;
      for (const dependent of dependents) {
        updated[dependent.id] = dependent.participant.id;
      }
      return updated;
    });
  };

  const addTicketGuests = (ticketId: number, ticketsMap: Map<number, Ticket>, newUsers: { [key: number]: number }) => {
    const ticket = ticketsMap.get(ticketId);
    if (!ticket?.dependents) return;

    const dependents = [...(ticket.dependents.adult ?? []), ...(ticket.dependents.child ?? [])];
    for (const dependent of dependents) {
      newUsers[dependent.id] = dependent.participant.id;
    }
  };

  const removeTicketGuests = (
    ticketId: number,
    ticketsMap: Map<number, Ticket>,
    newUsers: { [key: number]: number }
  ) => {
    const ticket = ticketsMap.get(ticketId);
    if (!ticket?.dependents) return;

    const dependents = [...(ticket.dependents.adult ?? []), ...(ticket.dependents.child ?? [])];
    for (const dependent of dependents) {
      delete newUsers[dependent.id];
    }
  };

  const toggeAllUsers = (users: { userId: number; ticketId: number }[], isChecked: boolean, tickets?: Ticket[]) => {
    const ticketsMap = tickets ? new Map(tickets.map((ticket) => [ticket.id, ticket])) : null;

    if (isChecked) {
      const newUsers: { [key: number]: number } = {};
      for (const user of users) {
        newUsers[user.ticketId] = user.userId;
        if (ticketsMap) {
          addTicketGuests(user.ticketId, ticketsMap, newUsers);
        }
      }
      setCheckedUsers((prev) => ({ ...prev, ...newUsers }));
      return;
    }

    const newUsers = { ...checkedUsers };
    for (const user of users) {
      delete newUsers[user.ticketId];
      if (ticketsMap) {
        removeTicketGuests(user.ticketId, ticketsMap, newUsers);
      }
    }
    setCheckedUsers(newUsers);
  };

  const isFilterApplied = Boolean(
    ticketFilter?.inviteFilter?.length ||
      ticketFilter?.ticketFilter?.length ||
      ticketFilter?.guestFilter ||
      ticketFilter?.registrationLinkSent != null ||
      ticketFilter?.registrationCompleted != null
  );

  const isUsersEmpty = !isLoading && isFilterApplied && !segments.length;

  const handleDeleteSegmentWithCleanup = (segment: ISegmentParticipants) => {
    onDeleteSegment(segment);
    setCheckedUsers({});
  };

  const removeTicketsFromSelection = (ids: number[]) => {
    setCheckedUsers((prev) => {
      if (!ids.length) return prev;
      const next = { ...prev };
      ids.forEach((id) => {
        delete next[id];
      });
      return next;
    });
    refetchEvent?.();
    refetchCategories?.();
  };

  const toggleViewMode = () => {
    setIsTreeView((prev) => !prev);
  };

  const handleSaveParticipants = useCallback(
    (userIds: number[], options?: { shouldRefetchSegments?: boolean }) => {
      setAddedTicketIds(userIds);
      if (!options?.shouldRefetchSegments) return;
      refetchSegmentsQuery();
    },
    [refetchSegmentsQuery]
  );

  const renderSegments = () => {
    if (segmentsFetching && !segments.length) {
      return (
        <div {...stylex.props(styles.skeletonsBlock)}>
          <div {...stylex.props(styles.segment, styles.segmentSkeleton)} />
        </div>
      );
    }
    if (isLoading) {
      return (
        <div {...stylex.props(styles.loader)}>
          <PreloaderCircular variant="secondary" size="large" />
        </div>
      );
    }
    return (
      <>
        {segments?.map((segment) => (
          <div key={segment.id} {...stylex.props(styles.segment)}>
            <SegmentCard
              onTicketDelete={removeTicketFromSelection}
              setCheckedUsers={setCheckedUsers}
              onToggle={(isOpen) => toggleAccordionHandler(isOpen)}
              segment={segment}
              checkedUsers={checkedUsers}
              toggleUser={toggleUser}
              toggleAllUsers={toggeAllUsers}
              onDeleteSegment={handleDeleteSegmentWithCleanup}
              addedTicketIds={addedTicketIds}
              onAddSegment={(users) => {
                openModaleHandler(segment, users);
              }}
              showAddForNoSegment={isCBGroup}
              ticketFilter={ticketFilter}
              isTreeView={isTreeView}
              registerRefetch={registerRefetch}
              refetchSegmentsQuery={refetchSegmentsQuery}
              eventId={eventId}
              refetchCategories={refetchCategories}
              eventDateEnd={eventDateEnd}
              eventDateStart={eventDateStart}
              showRegistration={registrationLinksSendingStarted}
              eventSettings={eventSettings}
            />
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      <ModalFilter
        showRegistration={registrationLinksSendingStarted}
        active={isFilterModalOpen}
        onClose={toggleFilterModal}
        onApply={(filter) => {
          setTicketFilter(filter);
          onFilterChange?.(filter);
        }}
      />
      <AddParticipantsModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        segment={{
          title: currentSegment.title,
          id: currentSegment.id,
          eventId: currentSegment.eventId || 0,
          countTicket: currentSegment.countTicket,
        }}
        invitedParticipants={invitedParticipants}
        onSaveParticipants={handleSaveParticipants}
        onTicketsDeleted={removeTicketsFromSelection}
      />

      <CommercialBlockParticipantsModal
        open={isCommercialBlockModalOpen}
        onClose={() => {
          setIsCommercialBlockModalOpen(false);
          setCommercialBlockFormData(null);
          setCommercialBlockTicketSegmentId(undefined);
        }}
        onlyMainGuest={!hasGeneratedLinks}
        formData={commercialBlockFormData ?? undefined}
        eventId={eventId ?? 0}
        ticketSegmentId={commercialBlockTicketSegmentId}
        mainGuestOptions={mainGuestOptions}
        onSuccess={() => {
          refetchSegmentsQuery();
        }}
      />

      <div {...stylex.props(styles.actionsContainer)}>
        <div {...stylex.props(styles.actions)}>
          <ParticipantsActionsDropdown
            eventId={eventId}
            formId={formId}
            onSendRegistration={onSendRegistration}
            registrationSending={registrationSending}
            checkedUsers={checkedUsers}
            setCheckedUsers={setCheckedUsers}
            sendTickets={sendTickets}
            sendInvitations={sendInvitations}
            onMoveToSegment={onMoveToSegment}
            onLoadingChange={setIsLoading}
            onTicketDelete={removeTicketFromSelection}
            eventDateEnd={eventDateEnd}
            eventSettings={eventSettings}
            refetchCategories={refetchCategories}
          />
        </div>

        <div {...stylex.props(styles.viewControls)}>
          <button {...stylex.props(styles.viewButton)} onClick={toggleViewMode}>
            {isTreeView ? <ListTreeIcon /> : <TableIcon />}
            <span {...stylex.props(styles.viewLabel)}>{isTreeView ? 'Таблица' : 'Дерево'}</span>
          </button>

          <button {...stylex.props(styles.filterButton)} onClick={toggleFilterModal}>
            <FunnelIcon color={colors.primaryBorder} />
            {isFilterApplied && <span {...stylex.props(styles.filterBadge)} />}
          </button>
        </div>
      </div>

      {isUsersEmpty && (
        <div {...stylex.props(styles.emptyState)}>
          <p>По выбранным критериям нет участников. Попробуйте изменить условия фильтрации.</p>
        </div>
      )}

      {renderSegments()}
      <div {...stylex.props(styles.containerBtn)}>
        <AddButton onClick={onAddSegment}>Добавить сегмент</AddButton>
      </div>
    </>
  );
};
